import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  IconShare3,
  IconCirclePlusFilled,
  IconLifebuoy,
  IconShoppingCart,
  IconHistory,
  IconChevronDown,
  IconChevronLeft,
  IconArrowBackUp,
  IconPillOff,
  IconSunrise,
  IconSun,
  IconSunset2,
  IconMoon,
} from "@tabler/icons-react";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import MFab from "@/components/ui/atoms/MFab";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import MTabs from "@/components/ui/molecules/MTabs";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { triggerNativePillboxShare } from "@/utils/nativeBridge";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { remindersService } from "@/services/reminders.service";
import { Reminders, ReminderType } from "@/types/reminders";
import { FilterOperation, FilterType } from "@/types/filter";
import {
  DaytimeSlot,
  DoseEvent,
  PendingChangeType,
  PillboxItem,
  PillboxPendingChange,
} from "@/types/pillbox";
import {
  formatFrequencyLine,
  formatMedicineTitle,
  getMemberLabel,
} from "./utils/formatMedicine";
import { computeSchedule } from "./utils/scheduleGenerator";
import { buildReminderPrefillFromPillboxItem } from "./utils/reminderPrefill";
import WeekDateStrip from "./components/WeekDateStrip";
import DaytimeSlotRow from "./components/DaytimeSlotRow";
import PeriodSection from "./components/PeriodSection";
import MedicineCard from "./components/MedicineCard";
import StopMedicationModal from "./components/StopMedicationModal";
import RemoveFromShoppingListModal from "./components/RemoveFromShoppingListModal";
import styles from "./PillboxLanding.module.css";

type ViewMode = "schedule" | "list";

const PERIOD_ICONS: Record<DaytimeSlot, React.ReactNode> = {
  morning: <IconSunrise size={24} stroke={1.5} color="#45168C" />,
  afternoon: <IconSun size={24} stroke={1.5} color="#45168C" />,
  evening: <IconSunset2 size={24} stroke={1.5} color="#45168C" />,
  night: <IconMoon size={24} stroke={1.5} color="#45168C" />,
};
const PERIOD_ORDER: DaytimeSlot[] = [
  "morning",
  "afternoon",
  "evening",
  "night",
];

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const result = new Date(d);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - result.getDay());
  return result;
}

function getWeekDates(anchor: Date): Date[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function bucketDoseTime(time: string): DaytimeSlot {
  const hour = parseInt(time.split(":")[0], 10);
  if (hour < 12) return "morning";
  if (hour < 16) return "afternoon";
  if (hour < 20) return "evening";
  return "night";
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const PillboxLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Present only when entered from a family-overview screen (e.g. Pillbox
  // overview), which passes it via navigate() state — plain tab-nav entry
  // to this route never sets it, so the back button stays hidden there.
  const backUrl = (location.state as { backUrl?: string } | null)?.backUrl;
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  const {
    fetchLedger,
    fetchPending,
    stopItem,
    toggleShoppingList,
    downloadSharePdf,
  } = usePillbox(memberId);

  const [viewMode, setViewMode] = useState<ViewMode>("schedule");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [ledger, setLedger] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<PillboxPendingChange[]>(
    []
  );
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [stopTarget, setStopTarget] = useState<PillboxItem | null>(null);
  const [stopping, setStopping] = useState(false);
  const [removeFromShoppingListTarget, setRemoveFromShoppingListTarget] =
    useState<PillboxItem | null>(null);
  const [removingFromShoppingList, setRemovingFromShoppingList] =
    useState(false);
  const [sharing, setSharing] = useState(false);
  const [highlightedPeriod, setHighlightedPeriod] =
    useState<DaytimeSlot | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Measures the live height of the sticky header (its content — the
  // pending-review banner, month/tabs row, Today chip, week strip — makes
  // it a variable height) so tapping a daytime period can scroll to a
  // position that isn't hidden underneath it. Exposed as a CSS custom
  // property (--pillbox-sticky-header-height) and consumed via
  // `scroll-margin-top` on each period header, so the native
  // scrollIntoView() does the actual scrolling — the previous hand-rolled
  // scrollTop math read stale layout values on mobile and silently landed
  // in the wrong place.
  const headerCardRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  useEffect(() => {
    const el = headerCardRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setStickyHeaderHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [remindersBySourceId, setRemindersBySourceId] = useState<
    Map<string, Reminders>
  >(new Map());

  // Restores the List/Schedule tab the user was on before opening a
  // medicine's detail page (see handleSelectListItem/handleSelectDose below
  // and MedicineDetail.tsx's back button). Synced via effect rather than a
  // lazy useState initializer — React Router can reuse this component
  // instance across navigations back to the same "/pillbox" route rather
  // than remounting it, in which case a one-time initializer would only
  // ever apply on the very first visit and every subsequent return would
  // silently keep whatever viewMode was already set (looking like it's
  // "stuck" on one tab). Only applies an explicit fromView — never forces a
  // switch when the state is absent, so unrelated navigations to /pillbox
  // (e.g. from the bottom nav) don't fight with the manual toggle buttons.
  useEffect(() => {
    const stateViewMode = (location.state as { viewMode?: ViewMode } | null)
      ?.viewMode;
    if (stateViewMode) {
      setViewMode(stateViewMode);
    }
  }, [location.state]);

  // Reminders set from Pillbox are linked back to their medicine via
  // sourceId (see reminderPrefill.ts) — fetch them all once so each card
  // can show its schedule inline and route to "Edit reminder" instead of
  // "Add reminder" once one exists.
  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    remindersService
      .getReminders(memberId, {
        filters: {
          type: {
            operation: FilterOperation.EQUAL,
            value: ReminderType.MEDICATION,
            type: FilterType.STRING,
          },
        },
        sort: [],
        pagination: { page: 1, pageSize: 200 },
      })
      .then((response) => {
        if (cancelled) return;
        const map = new Map<string, Reminders>();
        for (const reminder of response.data as Reminders[]) {
          if (reminder.sourceId) map.set(reminder.sourceId, reminder);
        }
        setRemindersBySourceId(map);
      })
      .catch(() => {
        if (!cancelled) setRemindersBySourceId(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  // Schedule is computed entirely client-side from the cached ledger, so
  // moving to any week — arbitrarily far forward or back — costs zero
  // network calls.
  const handlePrevWeek = useCallback(() => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() - 7);
      return next;
    });
  }, []);
  const handleNextWeek = useCallback(() => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
  }, []);
  const handleJumpToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, []);
  const handleDatePickerDone = useCallback((date: Date) => {
    setSelectedDate(date);
    setDatePickerOpen(false);
  }, []);

  const handleSelectPeriod = useCallback((period: DaytimeSlot) => {
    // `scroll-margin-top` on the header (see PeriodSection.module.css)
    // already reserves space for the sticky header, so a plain
    // scrollIntoView() lands correctly without any manual scrollTop math —
    // the previous hand-rolled version read stale layout values on mobile.
    document
      .querySelector(`[data-period-header="${period}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedPeriod(period);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(
      () => setHighlightedPeriod(null),
      900
    );
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current)
        clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const reloadTokenRef = useRef(0);

  // Fetches only the active ledger — the schedule itself is computed
  // locally from it (see computeSchedule), so navigating to any date
  // (past or future) never triggers another network call.
  const reload = useCallback(() => {
    if (!memberId) return;
    const token = ++reloadTokenRef.current;
    setLoading(true);
    fetchLedger("ACTIVE")
      .then((items) => {
        if (reloadTokenRef.current !== token) return;
        setLedger(items);
      })
      .catch(() => {
        if (reloadTokenRef.current !== token) return;
        setLedger([]);
      })
      .finally(() => {
        if (reloadTokenRef.current === token) setLoading(false);
      });
  }, [memberId, fetchLedger]);

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    fetchPending()
      .then((changes) => {
        if (!cancelled) setPendingChanges(changes);
      })
      .catch(() => {
        if (!cancelled) setPendingChanges([]);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, fetchPending]);

  useEffect(() => {
    reload();
  }, [reload]);

  // In-app navigation away and back already remounts this screen fresh (see
  // the viewMode-restore effect above) and re-triggers the effect above. But
  // backgrounding the app/device (lock screen, switching apps, the OS
  // suspending the WebView) doesn't navigate anywhere — the JS context is
  // just frozen and later resumed with whatever `ledger` it had, however
  // stale, since nothing was listening for the resume. This is the gap
  // behind issue #41: edit a medicine's end date, see it correctly in
  // Schedule, background/resume the app later, and the pre-edit schedule
  // reappears because nothing ever refetched.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        reload();
        fetchPending()
          .then(setPendingChanges)
          .catch(() => setPendingChanges([]));
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [reload, fetchPending]);

  const pendingAddChanges = useMemo(
    () =>
      pendingChanges.filter(
        (change) => change.changeType === PendingChangeType.ADD
      ),
    [pendingChanges]
  );
  const pendingPrescriptionCount = useMemo(
    () => new Set(pendingAddChanges.map((c) => c.sourcePrescriptionId)).size,
    [pendingAddChanges]
  );

  const weekDates = getWeekDates(selectedDate);
  const selectedDayKey = toDateKey(selectedDate);
  // Drives the "Today" jump-back chip — only shown once the visible week has
  // been navigated away from the one containing today.
  const weekIncludesToday = weekDates.some(isToday);
  // MDatePicker defaults its max-selectable date to today when no maxDate is
  // passed — without this, the schedule couldn't be browsed into the future
  // via the picker even though the prev/next week arrows already allow it.
  const maxPickerDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 5);
    return d;
  }, []);

  // Computed locally from the already-fetched ledger — no network call,
  // so this recomputes instantly for any week the user navigates to.
  const weekSchedule = useMemo(
    () => computeSchedule(ledger, weekDates.map(toDateKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ledger, weekDates[0]?.getTime()]
  );
  const selectedDay = weekSchedule.find((d) => d.date === selectedDayKey);

  const datesWithData = useMemo(() => {
    const set = new Set<string>();
    for (const day of weekSchedule) {
      if (day.doses.length > 0 || day.conditional.length > 0) set.add(day.date);
    }
    return set;
  }, [weekSchedule]);

  const dosesByPeriod = useMemo(() => {
    const grouped: Record<DaytimeSlot, DoseEvent[]> = {
      morning: [],
      afternoon: [],
      evening: [],
      night: [],
    };
    for (const dose of selectedDay?.doses ?? []) {
      grouped[bucketDoseTime(dose.time)].push(dose);
    }
    for (const period of PERIOD_ORDER) {
      grouped[period].sort((a, b) => a.time.localeCompare(b.time));
    }
    return grouped;
  }, [selectedDay]);

  const daytimeCounts: Record<DaytimeSlot, number> = {
    morning: dosesByPeriod.morning.length,
    afternoon: dosesByPeriod.afternoon.length,
    evening: dosesByPeriod.evening.length,
    night: dosesByPeriod.night.length,
  };

  const activeSlot = isToday(selectedDate)
    ? bucketDoseTime(`${String(new Date().getHours()).padStart(2, "0")}:00`)
    : null;

  const scheduledLedger = ledger.filter(
    (item) => item.timingRuleType !== "CONDITIONAL"
  );
  const scheduledMedsCount = scheduledLedger.length;

  // List view only shows scheduled medicines — SOS/conditional items live
  // exclusively on the SOS screen.
  const alphabeticalGroups = useMemo(() => {
    const groups = new Map<string, PillboxItem[]>();
    const sorted = [...scheduledLedger].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    for (const item of sorted) {
      const letter = item.name.charAt(0).toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(item);
    }
    return Array.from(groups.entries());
  }, [scheduledLedger]);

  const itemsById = useMemo(
    () => new Map(ledger.map((item) => [item.id, item])),
    [ledger]
  );

  const handleAdd = () =>
    navigate("/pillbox/add-medicine", { state: { fromView: viewMode } });
  const handleSelectDose = (dose: DoseEvent) =>
    navigate(`/pillbox/${memberId}/${dose.itemType}/${dose.itemId}`, {
      state: { fromView: "schedule" },
    });
  const handleSelectListItem = (item: PillboxItem) =>
    navigate(`/pillbox/${memberId}/${item.itemType}/${item.id}`, {
      state: { fromView: "list" },
    });

  const handleEditDetails = (item: PillboxItem) =>
    navigate("/pillbox/add-medicine", {
      // Forward the current tab so AddMedicine's own Back/Save navigation
      // (see AddMedicineNavState.fromView) restores the same one — this is
      // a direct List/Schedule row action, not routed through
      // MedicineDetail, so it needs its own fromView the same way
      // MedicineDetail.handleEditDetails does.
      state: { mode: "edit", item, fromView: viewMode },
    });
  const handleEditDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleEditDetails(item);
  };

  const handleStopRequest = (item: PillboxItem) => setStopTarget(item);
  const handleStopDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleStopRequest(item);
  };

  const handleAddReminder = (item: PillboxItem) => {
    const prefill = buildReminderPrefillFromPillboxItem(item);
    if (!prefill) return;
    navigate("/reminders/medication", { state: { pillboxPrefill: prefill } });
  };
  const getReminderPrefillForDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    return item ? buildReminderPrefillFromPillboxItem(item) : null;
  };

  const getReminderForItem = (item: PillboxItem) =>
    remindersBySourceId.get(item.id) ?? null;
  const getReminderForDose = (dose: DoseEvent) =>
    remindersBySourceId.get(dose.itemId) ?? null;
  const handleEditReminder = (reminder: Reminders) =>
    navigate("/reminders/medication", { state: { reminder } });

  const performToggleShoppingList = async (
    item: PillboxItem,
    nextInShoppingList: boolean
  ) => {
    try {
      await toggleShoppingList(item.itemType, item.id, nextInShoppingList);
      showToast({
        variant: "success",
        message: t(
          nextInShoppingList
            ? "pillbox.shoppingList.addedToast"
            : "pillbox.shoppingList.removedToast",
          { name: formatMedicineTitle(item) }
        ),
      });
      reload();
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    }
  };
  // Removing needs confirmation (same as the dedicated Shopping List
  // screen) — adding doesn't, so that stays a single tap everywhere.
  const handleToggleShoppingList = (item: PillboxItem) => {
    if (item.inShoppingList) {
      setRemoveFromShoppingListTarget(item);
    } else {
      performToggleShoppingList(item, true);
    }
  };
  const handleToggleShoppingListDose = (dose: DoseEvent) => {
    const item = itemsById.get(dose.itemId);
    if (item) handleToggleShoppingList(item);
  };
  const handleConfirmRemoveFromShoppingList = async () => {
    const item = removeFromShoppingListTarget;
    if (!item) return;
    setRemovingFromShoppingList(true);
    try {
      await performToggleShoppingList(item, false);
      setRemoveFromShoppingListTarget(null);
    } finally {
      setRemovingFromShoppingList(false);
    }
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const view = viewMode === "list" ? "list" : "schedule";

      // 1. Native webview: ask the shell to fetch the PDF and present the OS
      //    share sheet — same path as vitals/lab-insights share.
      const handed = triggerNativePillboxShare({
        memberId,
        memberName: memberDisplayName,
        view,
        date: view === "schedule" ? selectedDayKey : undefined,
      });
      if (handed) return;

      // 2. Web fallback: download the PDF directly.
      const blob = await downloadSharePdf(
        view,
        view === "schedule" ? selectedDayKey : undefined
      );
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shareError"),
      });
    } finally {
      setSharing(false);
    }
  };

  const handleConfirmStop = async () => {
    if (!stopTarget) return;
    setStopping(true);
    try {
      await stopItem(stopTarget.itemType, stopTarget.id);
      showToast({
        variant: "success",
        message: t("pillbox.stopModal.successMessage", {
          name: formatMedicineTitle(stopTarget),
        }),
      });
      setStopTarget(null);
      reload();
    } finally {
      setStopping(false);
    }
  };

  const monthYearLabel = selectedDate.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
  });

  return (
    <div
      className={styles.root}
      style={
        {
          "--pillbox-sticky-header-height": `${stickyHeaderHeight}px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.headerCard} ref={headerCardRef}>
        <div className={styles.topBar}>
          {backUrl ? (
            <button
              type="button"
              className={styles.headerBackButton}
              onClick={() => navigate(backUrl)}
              aria-label={t("common.back")}
            >
              <IconChevronLeft size={24} className={styles.headerIcon} />
            </button>
          ) : (
            <div className={styles.iconSpacer} />
          )}
          <h1 className={styles.title}>{t("pillbox.title")}</h1>
          <button
            type="button"
            className={styles.headerShareButton}
            onClick={handleShare}
            disabled={sharing}
            aria-label={t("pillbox.share")}
          >
            <IconShare3 size={24} stroke={1.5} className={styles.headerIcon} />
          </button>
        </div>
        <div className={styles.switchMemberRow}>
          <MProfilePicker />
          <button
            type="button"
            className={styles.headerAddButton}
            onClick={handleAdd}
            aria-label={t("pillbox.add")}
          >
            <IconCirclePlusFilled size={32} />
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.quickActionsRow}>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() =>
              navigate("/pillbox/sos", { state: { fromView: viewMode } })
            }
          >
            <span className={styles.quickActionIcon}>
              <IconLifebuoy size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>{t("pillbox.sos")}</span>
          </button>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() =>
              navigate("/pillbox/shopping-list", {
                state: { fromView: viewMode },
              })
            }
          >
            <span className={styles.quickActionIcon}>
              <IconShoppingCart size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>
              {t("pillbox.shoppingList.title")}
            </span>
          </button>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() =>
              navigate("/pillbox/past-medications", {
                state: { fromView: viewMode },
              })
            }
          >
            <span className={styles.quickActionIcon}>
              <IconHistory size={32} stroke={1.5} />
            </span>
            <span className={styles.quickActionLabel}>
              {t("pillbox.pastMedications")}
            </span>
          </button>
        </div>

        {!bannerDismissed && pendingAddChanges.length > 0 && (
          <div className={styles.pendingBanner}>
            <p className={styles.pendingBannerTitle}>
              {t("pillbox.review.bannerTitle", {
                count: pendingAddChanges.length,
                member: getMemberLabel(memberInfo, "for"),
              })}
            </p>
            <p className={styles.pendingBannerSubtitle}>
              {t("pillbox.review.bannerSubtitle", {
                count: pendingPrescriptionCount,
                member: getMemberLabel(memberInfo, "possessive"),
              })}
            </p>
            <div className={styles.pendingBannerActions}>
              <button
                type="button"
                className={styles.pendingBannerIgnore}
                onClick={() => setBannerDismissed(true)}
              >
                {t("pillbox.review.ignore")}
              </button>
              <button
                type="button"
                className={styles.pendingBannerReview}
                onClick={() => navigate("/pillbox/review")}
              >
                {t("pillbox.review.review")}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className={styles.loaderWrapper}>
            <MLoader size={32} />
          </div>
        ) : viewMode === "schedule" ? (
          <>
            <div className={styles.scheduleHeaderCluster}>
              <div className={styles.sectionHeaderRow}>
                <button
                  type="button"
                  className={styles.monthHeaderButton}
                  onClick={() => setDatePickerOpen(true)}
                >
                  <span className={styles.sectionHeaderTitle}>
                    {monthYearLabel}
                  </span>
                  <IconChevronDown size={16} />
                </button>
                <div className={styles.tabsWrapper}>
                  <MTabs
                    activeTab={viewMode}
                    tabs={[
                      { value: "schedule", label: t("pillbox.scheduleView") },
                      { value: "list", label: t("pillbox.listView") },
                    ]}
                    onTabChange={(v) => setViewMode(v as ViewMode)}
                    variant="pill"
                  />
                </div>
              </div>

              <AnimatePresence initial={false}>
                {!weekIncludesToday && (
                  <motion.button
                    type="button"
                    className={styles.todayChip}
                    onClick={handleJumpToToday}
                    initial={{ opacity: 0, y: -6, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.95 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <IconArrowBackUp size={12} />
                    {t("pillbox.today")}
                  </motion.button>
                )}
              </AnimatePresence>

              <WeekDateStrip
                weekDates={weekDates}
                selectedDate={selectedDate}
                datesWithData={datesWithData}
                onSelect={setSelectedDate}
                onPrevWeek={handlePrevWeek}
                onNextWeek={handleNextWeek}
                prevWeekLabel={t("pillbox.previousWeek")}
                nextWeekLabel={t("pillbox.nextWeek")}
              />
            </div>

            <DaytimeSlotRow
              counts={daytimeCounts}
              activeSlot={activeSlot}
              onSelect={handleSelectPeriod}
            />

            <div className={styles.periodSections}>
              {PERIOD_ORDER.map((period) => (
                <PeriodSection
                  key={period}
                  id={`pillbox-period-${period}`}
                  period={period}
                  icon={PERIOD_ICONS[period]}
                  doses={dosesByPeriod[period]}
                  highlighted={highlightedPeriod === period}
                  onSelectDose={handleSelectDose}
                  onEditDose={handleEditDose}
                  onStopDose={handleStopDose}
                  getReminderPrefillForDose={getReminderPrefillForDose}
                  getReminderForDose={getReminderForDose}
                  getInShoppingListForDose={(dose) =>
                    itemsById.get(dose.itemId)?.inShoppingList ?? false
                  }
                  onToggleShoppingListDose={handleToggleShoppingListDose}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={styles.sectionHeaderRow}>
              <span className={styles.sectionHeaderTitle}>
                {t("pillbox.scheduledMeds", { count: scheduledMedsCount })}
              </span>
              <div className={styles.tabsWrapper}>
                <MTabs
                  activeTab={viewMode}
                  tabs={[
                    { value: "schedule", label: t("pillbox.scheduleView") },
                    { value: "list", label: t("pillbox.listView") },
                  ]}
                  onTabChange={(v) => setViewMode(v as ViewMode)}
                  variant="pill"
                />
              </div>
            </div>

            {scheduledLedger.length === 0 ? (
              <MEmptyCard
                className={styles.emptyCard}
                icon={<IconPillOff size={64} stroke={1.5} color="#AD95D1" />}
                primaryTitle={t("pillbox.emptyList.title")}
              />
            ) : (
              <div className={styles.alphabeticalList}>
                {alphabeticalGroups.map(([letter, items]) => (
                  <div className={styles.alphaSection} key={letter}>
                    <div className={styles.alphaHeader}>
                      <span className={styles.alphaLetter}>{letter}</span>
                      <div className={styles.alphaDivider} />
                    </div>
                    {items.map((item) => {
                      const reminder = getReminderForItem(item);
                      return (
                        <MedicineCard
                          key={item.id}
                          itemType={item.itemType}
                          title={formatMedicineTitle(item)}
                          frequencyLine={formatFrequencyLine(
                            item,
                            item.recurrenceIntervalDays
                          )}
                          trailingIcon="dots"
                          onClick={() => handleSelectListItem(item)}
                          onEditDetails={() => handleEditDetails(item)}
                          reminder={reminder}
                          onAddReminder={
                            !reminder &&
                            buildReminderPrefillFromPillboxItem(item)
                              ? () => handleAddReminder(item)
                              : undefined
                          }
                          onEditReminder={
                            reminder
                              ? () => handleEditReminder(reminder)
                              : undefined
                          }
                          inShoppingList={item.inShoppingList}
                          onToggleShoppingList={() =>
                            handleToggleShoppingList(item)
                          }
                          onStopMedication={() => handleStopRequest(item)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <MFab />

      <StopMedicationModal
        open={!!stopTarget}
        onClose={() => setStopTarget(null)}
        onConfirm={handleConfirmStop}
        loading={stopping}
      />

      <RemoveFromShoppingListModal
        open={!!removeFromShoppingListTarget}
        itemName={
          removeFromShoppingListTarget
            ? formatMedicineTitle(removeFromShoppingListTarget)
            : ""
        }
        onClose={() => setRemoveFromShoppingListTarget(null)}
        onConfirm={handleConfirmRemoveFromShoppingList}
        loading={removingFromShoppingList}
      />

      <MDatePicker
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onDone={handleDatePickerDone}
        initialValue={selectedDate}
        title={t("pillbox.selectDate")}
        maxDate={maxPickerDate}
      />
    </div>
  );
};

export default PillboxLanding;
